import type { SegmentViewDto } from '@carbonwebmail/contracts'
import { EllipsisMenu } from '../../ui/EllipsisMenu.js'
import { MenuItem } from '../../ui/Menu.js'

/**
 * The tabs above the segments board.
 *
 * **A tab is a view, not a segment.** On this board that is worth saying
 * loudly, because the rows underneath *are* segments: pressing a tab changes
 * what you are looking at and never what a campaign reaches.
 *
 * A board with no tabs is still the board — nothing is seeded, because what
 * somebody wants a tab for depends on the segments they have made. The `+`
 * keeps what is on screen: the search, the order and the hidden columns.
 *
 * Each tab carries its own `⋯` rather than a shared one, so deleting a view is
 * where the view is instead of behind a selection somewhere else.
 */
export function SegmentViewTabs({
  views,
  chosen,
  onChoose,
  onSave,
  onRemove,
}: {
  readonly views: readonly SegmentViewDto[]
  readonly chosen: string | undefined
  readonly onChoose: (viewId: string | undefined) => void
  readonly onSave: () => void
  readonly onRemove: (viewId: string) => void
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-line border-b">
      {/*
        Always first and always there. `/segments` with no view is every
        segment, which is where the sidebar leads and what somebody returning
        to the screen expects.
      */}
      <Tab chosen={chosen === undefined} onClick={() => onChoose(undefined)}>
        All segments
      </Tab>

      {views.map((view) => (
        <span className="group/tab flex items-center gap-1" key={view.viewId}>
          <Tab chosen={chosen === view.viewId} onClick={() => onChoose(view.viewId)}>
            {view.name}
          </Tab>
          <ViewMenu name={view.name} onRemove={() => onRemove(view.viewId)} />
        </span>
      ))}

      <button
        aria-label="Save this arrangement as a view"
        className="-mb-px border-transparent border-b-2 py-2.5 font-semibold text-[13px] text-t3 hover:text-t1"
        onClick={onSave}
        type="button"
      >
        +
      </button>
    </div>
  )
}

function Tab({
  chosen,
  onClick,
  children,
}: {
  readonly chosen: boolean
  readonly onClick: () => void
  readonly children: React.ReactNode
}) {
  return (
    <button
      aria-current={chosen ? 'page' : undefined}
      className={`-mb-px border-b-2 py-2.5 font-semibold text-[13px] ${
        chosen ? 'border-brand text-brand' : 'border-transparent text-t2 hover:text-t1'
      }`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  )
}

/** Deleting a view removes a way of looking. No segment goes with it. */
/** Deleting a view removes a way of looking. No segment goes with it. */
function ViewMenu({ name, onRemove }: { readonly name: string; readonly onRemove: () => void }) {
  return (
    <EllipsisMenu hover="tab" label={`Options for the ${name} view`} width="w-56">
      {(choose) => (
        <MenuItem danger onClick={choose(onRemove)}>
          Delete this view
        </MenuItem>
      )}
    </EllipsisMenu>
  )
}
